import { Iso8601DateTimeString } from '../../../../types/Iso8601DateTimeString';
import { ProductContext } from '../../../../types/ProductContext';
import { SamplekitMessage } from '../../../../types/SamplekitMessage';
import { ValidationResponse } from '../../../../types/ValidationResponse';
import { OfferMessage } from '../../../offer/model/OfferModel';
import { OfferState } from '../../../offer/model/OfferState';
import { OrderMessage, OrderState } from '../../../order/model/OrderModel';
import { ItemSourceValues } from '../../models/ItemModel';
import { authorizedAxiosInstance } from '../instances/AuthorizedAxiosInstance';

interface CreateOrderItemRequest {
    productId: string | null;
    packType: string;
    unitSize: number | null;
    units: number | null;
    unit: string;
}

export interface CreateOrderRequest {
    vesselId: string | null;
    portId: string | null;
    buyerReference: string | null;
    agentId: string | null;
    comment: string | null;
    creationComment: string | null;
    receiverSupplierIds: string[] | null;
    receiverContractIds?: string[] | null;
    samplekits: SamplekitMessage[] | null;
    spot: boolean | null;
    state: OrderState | null;
    chosenOfferId: string | null;
    type: ProductContext;
    fileIds: string[] | null;
    scheduleSharingEnabled?: boolean | null;
}

type CreateOrderItemWithVolumeRequest = CreateOrderItemRequest & {
    volume: number | null;
    deleted: boolean;
    sourceValues?: ItemSourceValues | null;
};

type CreateOrderWithItemsRequest = CreateOrderRequest & {
    dateDelivery: Iso8601DateTimeString | null;
    items: CreateOrderItemWithVolumeRequest[];
};

export interface CreateOrderResponse extends CreateOrderWithItemsRequest {
    id: string;
}

export type UpdateOrderRequest = CreateOrderWithItemsRequest & {
    cancelReason: string | null;
};

export const createOrder = async (order: CreateOrderWithItemsRequest): Promise<CreateOrderResponse> => {
    const response = await authorizedAxiosInstance.post('/v2/orders', order);

    return response.data;
};

interface UpdateOrderParams {
    orderId: string;
    order: UpdateOrderRequest;
}

export const updateOrder = async ({ orderId, order }: UpdateOrderParams): Promise<OrderMessage> => {
    const response = await authorizedAxiosInstance.put(`/v2/orders/${orderId}`, order);

    return response.data;
};
type OrderValidationRequest = Partial<CreateOrderRequest> & {
    dateDelivery?: Iso8601DateTimeString | null;
    items?: Partial<CreateOrderItemWithVolumeRequest>[];
};

export const validateOrderCreation = async (orderDetails: OrderValidationRequest): Promise<ValidationResponse> => {
    const response = await authorizedAxiosInstance.post('/v2/orders/validation/create', {
        ...orderDetails,
        type: ProductContext.LUBES,
    });
    return response.data;
};

export const validateOrderUpdate = async (
    orderDetails: OrderValidationRequest,
    orderId: string
): Promise<ValidationResponse> => {
    const response = await authorizedAxiosInstance.post(`/v2/orders/validation/update/${orderId}`, {
        ...orderDetails,
        type: ProductContext.LUBES,
    });
    return response.data;
};

interface CreateMessageParams {
    offerId: string;
    message: string;
}

export const createMessage = async ({ offerId, message }: CreateMessageParams): Promise<any> => {
    const response = await authorizedAxiosInstance.post(`/v2/offers/${offerId}/message`, { message });

    return response.data;
};

export interface MarkOrderReadParams {
    orderId: string;
    read: boolean;
}

export const markOrderRead = async ({ orderId, read }: MarkOrderReadParams): Promise<void> => {
    await authorizedAxiosInstance.put(`/v2/orders/${orderId}/read/${read}`);
};
export const redispatchOrder = async (orderId: string): Promise<OrderMessage> => {
    const response = await authorizedAxiosInstance.put(`/v2/orders/${orderId}/redispatch`);

    return response.data;
};
export const deleteOrder = async (orderId: string): Promise<void> => {
    await authorizedAxiosInstance.delete(`/v2/orders/${orderId}`);
};

interface MarkOfferReadParams {
    offerId: string;
    read: boolean;
}

export const markOfferRead = async ({ offerId, read }: MarkOfferReadParams): Promise<any> => {
    const response = await authorizedAxiosInstance.put(`/v2/offers/${offerId}/read/${read}`);

    return response.data;
};

interface UpdateOfferParams {
    offerId: string;
    offer: unknown;
}

export const updateOffer = async ({ offerId, offer }: UpdateOfferParams): Promise<OfferMessage> => {
    const response = await authorizedAxiosInstance.put(`/v2/offers/${offerId}`, offer);

    return response.data;
};

interface PatchOfferParams {
    offerId: string;
    offer: PatchOfferPayload;
}

interface PatchOfferPayload {
    state: OfferState;
}

export const patchOffer = async ({ offerId, offer }: PatchOfferParams): Promise<OfferMessage> => {
    const response = await authorizedAxiosInstance.patch(`/v2/offers/${offerId}`, offer);

    return response.data;
};

interface ValidateOfferParams {
    offerId: string;
    offer: unknown;
}

export const validateOffer = async ({ offerId, offer }: ValidateOfferParams): Promise<ValidationResponse> => {
    const response = await authorizedAxiosInstance.post(`/v2/offers/validation/${offerId}`, offer);
    return response.data;
};
